export const PLAN_PRICES = {
  starter: { monthly: 39, annual: 390 },
  professional: { monthly: 79, annual: 790 },
  business: { monthly: 149, annual: 1490 },
} as const;

export const ANNUAL_SAVINGS_PERCENT = 17;

export type BillingInterval = 'monthly' | 'annual';

// Helper function to get price based on interval
export const getPlanPrice = (plan: UserPlan, interval: BillingInterval = 'monthly'): number => {
  const prices = PLAN_PRICES[plan];
  return interval === 'annual' ? prices.annual : prices.monthly;
};

// Helper to get monthly equivalent price (for annual, divide by 12)
export const getMonthlyEquivalent = (plan: UserPlan, interval: BillingInterval = 'monthly'): number => {
  const prices = PLAN_PRICES[plan];
  return interval === 'annual' ? Math.round(prices.annual / 12) : prices.monthly;
};

export const PLAN_LIMITS = {
  starter: {
    pages: 2,
    submissions: 100,
    teamMembers: 5,
    campaigns: 1,
  },
  professional: {
    pages: 10,
    submissions: Infinity,
    teamMembers: 15,
    campaigns: 3,
  },
  business: {
    pages: Infinity,
    submissions: Infinity,
    teamMembers: Infinity,
    campaigns: Infinity,
  },
} as const;

export type UserPlan = 'starter' | 'professional' | 'business';

export const PLAN_FEATURES = {
  starter: {
    name: "Starter",
    description: "Perfect for small teams getting started",
    features: [
      `${PLAN_LIMITS.starter.pages} feedback pages`,
      `${PLAN_LIMITS.starter.submissions} submissions per month`,
      `${PLAN_LIMITS.starter.teamMembers} team members`,
      `${PLAN_LIMITS.starter.campaigns} scheduled campaign`,
      "30-day data retention",
      "Basic AI sentiment analysis",
      "Logo branding",
      "Email notifications",
      "Export to CSV",
      "Email support (48h)",
    ],
  },
  professional: {
    name: "Professional",
    description: "For growing teams with advanced needs",
    popular: true,
    features: [
      `${PLAN_LIMITS.professional.pages} feedback pages`,
      "Unlimited submissions",
      `${PLAN_LIMITS.professional.teamMembers} team members`,
      `${PLAN_LIMITS.professional.campaigns} scheduled campaigns`,
      "30-day data retention",
      "Advanced AI insights & theme clustering",
      "Risk classification & escalation",
      "Action tracker with kanban",
      "Logo & colors branding",
      "Priority email support (24h)",
      "Export to CSV & PDF",
      "Read-only API access",
      "Scheduled weekly/monthly reports",
    ],
  },
  business: {
    name: "Business",
    description: "Enterprise-grade features and support",
    features: [
      "Unlimited feedback pages",
      "Unlimited submissions",
      "Unlimited team members",
      "Unlimited scheduled campaigns",
      "30-day data retention",
      "HR Advisor AI + custom training",
      "Advanced role-based permissions",
      "Custom domain",
      "Full + White-label branding",
      "Full API access",
      "Webhooks for custom integrations",
      "SSO/SAML authentication (coming soon)",
      "Advanced analytics dashboard",
      "Dedicated account manager",
      "Priority support (4h response)",
      "Quarterly business reviews",
    ],
  },
} as const;
